import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SparklesIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import PlatformRing from '../shared/PlatformRing';

const BAND_LABEL = {
  strong: { text: 'Strong', cls: 'text-green-600 dark:text-green-400' },
  moderate: { text: 'Moderate', cls: 'text-amber-600 dark:text-amber-400' },
  weak: { text: 'Needs work', cls: 'text-red-600 dark:text-red-400' },
  insufficient: { text: 'Learning', cls: 'text-gray-400' },
};

const formatWindow = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  } catch {
    return null;
  }
};

/**
 * Pre-Flight card — scores the draft against the creator's Content DNA before publishing.
 * Advisory and non-blocking: it never disables the Publish button. Re-scores (debounced)
 * when the target platforms change. Rendered only when the creator_intelligence flag is on
 * for the user; otherwise it renders nothing, so the wizard is unchanged for everyone else.
 */
export default function PreFlightCard({ contentId, platforms }) {
  const { enabled, loading: flagLoading } = useFeature('creator_intelligence');
  const isDark = useSelector((s) => s.theme?.mode === 'dark');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const platformsKey = (platforms || []).join(',');

  useEffect(() => {
    if (!enabled || !contentId || !(platforms && platforms.length)) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await api.post(`/api/v1/intelligence/preflight/${contentId}`, { targets: platforms });
        setData(res.data.data);
      } catch {
        setData(null); // e.g. not targeted / no DNA yet — stay silent
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [enabled, contentId, platformsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (flagLoading || !enabled) return null;

  const scores = data?.scores || [];
  const fixes = data?.fixes || [];
  const bestWindow = data?.bestWindow?.at ? formatWindow(data.bestWindow.at) : null;

  return (
    <div className="rounded-xl border border-violet-100 dark:border-violet-900/40 bg-violet-50/40 dark:bg-violet-900/10 p-4">
      <div className="flex items-center gap-2 mb-3">
        <SparklesIcon className="w-4 h-4 text-violet-600" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white">Pre-Flight</span>
        {loading && <span className="text-xs text-gray-400">scoring…</span>}
        <span className="ml-auto text-[11px] text-gray-400">based on your Content DNA</span>
      </div>

      {scores.length === 0 && !loading && (
        <p className="text-xs text-gray-400">Publish a few more posts to unlock Pre-Flight scoring.</p>
      )}

      {scores.length > 0 && (
        <div className="flex flex-wrap gap-4 mb-3">
          {scores.map((s) => {
            const band = BAND_LABEL[s.band] || BAND_LABEL.insufficient;
            return (
              <div key={s.platform} className="flex flex-col items-center gap-1">
                {s.score != null ? (
                  <PlatformRing platform={s.platform} score={s.score} size={64} showScore isDark={isDark} />
                ) : (
                  <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-200 dark:border-dark-600 flex items-center justify-center text-[10px] text-gray-400">
                    learning
                  </div>
                )}
                <span className={`text-[11px] font-medium ${band.cls}`}>{band.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {fixes.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Ways to lift your score</p>
          {fixes.map((f, i) => (
            <div key={`${f.attribute}-${i}`} className="flex items-center gap-2 text-xs">
              <span className="text-gray-700 dark:text-gray-300">{f.suggestion}</span>
              <span className="ml-auto text-[10px] font-semibold text-violet-600 dark:text-violet-400 tabular-nums">
                +{f.estLift}%
              </span>
            </div>
          ))}
        </div>
      )}

      {bestWindow && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-violet-100 dark:border-violet-900/40 text-xs text-gray-600 dark:text-gray-300">
          <ClockIcon className="w-3.5 h-3.5 text-violet-500" />
          Best time to post: <span className="font-semibold">{bestWindow}</span>
        </div>
      )}
    </div>
  );
}
